'use client';

import { c as _c } from "react/compiler-runtime";
import { Check } from 'lucide-react';
import { useId } from 'react';
import { formatMoney } from '@/lib/money';
import { formatMembershipCadence, TOOLBOX_MEMBERSHIP_BENEFITS, TOOLBOX_MEMBERSHIP_TITLE, type ToolboxMembershipOffer } from '@/lib/toolbox-membership';
interface ToolboxMembershipRowProps {
  offer: ToolboxMembershipOffer;
  /** Whether the Toolbox is in the cart. */
  checked: boolean;
  /** Tick adds the Toolbox in the shopper's size (then opens the size sheet); untick removes it. */
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  moneyLocale?: string;
}

/**
 * The Toolbox membership row under the add-to-cart button, in the shape of
 * the original storefront's: a filled red check, "TOOLBOX MEMBERSHIP" with
 * the benefits line under it, and the regular vs member price on the right.
 * The row is the whole control — ticking it adds the Toolbox straight to the
 * cart in the tee size chosen above, and the size sheet opens to confirm.
 */
export function ToolboxMembershipRow(t0) {
  const $ = _c(34);
  const {
    offer,
    checked,
    onCheckedChange,
    disabled: t1,
    moneyLocale
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const checkboxId = useId();
  const hasDiscount = Number.parseFloat(offer.oneTimePrice.amount) > Number.parseFloat(offer.memberPrice.amount);
  const t2 = `flex cursor-pointer items-center gap-2.5 rounded border bg-neutral-50 px-2.5 py-1.5 transition-colors ${checked ? "border-neutral-400" : "border-neutral-200"} ${disabled ? "opacity-60" : ""}`;
  let t3;
  if ($[0] !== onCheckedChange) {
    t3 = event => onCheckedChange(event.target.checked);
    $[0] = onCheckedChange;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== checkboxId || $[3] !== checked || $[4] !== disabled || $[5] !== t3) {
    t4 = <input id={checkboxId} type="checkbox" className="sr-only" checked={checked} disabled={disabled} onChange={t3} />;
    $[2] = checkboxId;
    $[3] = checked;
    $[4] = disabled;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = `flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm border transition-colors ${checked ? "border-brand-accent bg-brand-accent text-white" : "border-neutral-300 bg-white text-transparent"}`;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Check className="h-3 w-3" strokeWidth={3} />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5) {
    t7 = <span aria-hidden="true" className={t5}>{t6}</span>;
    $[8] = t5;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="min-w-0 flex-1"><span className="block truncate text-[11px] font-bold uppercase leading-tight tracking-wide text-neutral-900">{TOOLBOX_MEMBERSHIP_TITLE}</span><span className="block truncate text-[11px] leading-snug text-neutral-600">{TOOLBOX_MEMBERSHIP_BENEFITS}</span></span>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== hasDiscount || $[12] !== moneyLocale || $[13] !== offer.oneTimePrice) {
    t9 = hasDiscount && <span className="block text-[10px] text-neutral-400 line-through">Reg: {formatMoney(offer.oneTimePrice, moneyLocale)}</span>;
    $[11] = hasDiscount;
    $[12] = moneyLocale;
    $[13] = offer.oneTimePrice;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== moneyLocale || $[16] !== offer.memberPrice) {
    t10 = formatMoney(offer.memberPrice, moneyLocale);
    $[15] = moneyLocale;
    $[16] = offer.memberPrice;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== offer.frequencyPhrase) {
    t11 = formatMembershipCadence(offer.frequencyPhrase);
    $[18] = offer.frequencyPhrase;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <span className="text-[9px] font-semibold">{t11}</span>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t12) {
    t13 = <span className="block text-[12px] font-bold text-brand-accent">Member: {t10}{t12}</span>;
    $[22] = t10;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t9) {
    t14 = <span className="flex-shrink-0 text-right leading-tight">{t9}{t13}</span>;
    $[25] = t13;
    $[26] = t9;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== checkboxId || $[29] !== t14 || $[30] !== t2 || $[31] !== t4 || $[32] !== t7) {
    t15 = <label htmlFor={checkboxId} className={t2}>{t4}{t7}{t8}{t14}</label>;
    $[28] = checkboxId;
    $[29] = t14;
    $[30] = t2;
    $[31] = t4;
    $[32] = t7;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}